import { inject, Injectable } from '@angular/core';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { NotificationService } from '../../../../core/services/NotificationService';
import { OnlineOrderStatuses } from '../enums/OnlineOrderStatuses';
import { OnlineOrdersStore } from '../stores/OnlineOrdersStore';
import { OnlineOrdersService } from './OnlineOrdersService';

@Injectable()
export class OnlineOrdersScreenService {

  private readonly _onlineOrdersStore = inject(OnlineOrdersStore);
  private readonly _onlineOrdersService = inject(OnlineOrdersService);
  private readonly _notificationService = inject(NotificationService);

  public readonly orders = this._onlineOrdersStore.orders;
  public readonly status = this._onlineOrdersStore.status;
  public readonly pagination = this._onlineOrdersStore.pagination;
  public readonly isLoading = this._onlineOrdersStore.isLoading;

  public resetForOpen(): void {
    this._onlineOrdersStore.status.set(OnlineOrderStatuses.Open);
    this._onlineOrdersStore.search.set('');
    this._onlineOrdersStore.orders.set([]);
    this._onlineOrdersStore.pagination.set({ page: 1, results: 10, totalResults: 0 });
  }

  public setStatus(status: OnlineOrderStatuses): void {
    this._onlineOrdersStore.status.set(status);
    this._onlineOrdersStore.orders.set([]);
    this._onlineOrdersStore.pagination.update(pagination => ({ ...pagination, page: 1, totalResults: 0 }));
    this.loadOrders();
  }

  public setSearch(search: string): void {
    this._onlineOrdersStore.search.set(search ?? '');
    this._onlineOrdersStore.orders.set([]);
    this._onlineOrdersStore.pagination.update(pagination => ({ ...pagination, page: 1, totalResults: 0 }));
    this.loadOrders();
  }

  public setPage(page: number): void {
    this._onlineOrdersStore.pagination.update(pagination => ({ ...pagination, page }));
    this.loadOrders();
  }

  public loadOrders(): void {
    this._onlineOrdersStore.isLoading.set(true);

    this._onlineOrdersService.getOnlineOrders(
      this._onlineOrdersStore.status(),
      this._onlineOrdersStore.pagination(),
      this._onlineOrdersStore.search()
    )
      .pipe(
        finalize(() => this._onlineOrdersStore.isLoading.set(false))
      )
      .subscribe({
        next: res => {
          this._onlineOrdersStore.orders.set(res.data?.results ?? []);
          if (res.data?.info) {
            this._onlineOrdersStore.pagination.set({
              page: res.data.info.page,
              results: res.data.info.results,
              totalResults: res.data.info.totalResults
            });
          }
        },
        error: ({ error }: ApiErrorResponse) => {
          this._onlineOrdersStore.orders.set([]);
          this._onlineOrdersStore.pagination.update(pagination => ({ ...pagination, totalResults: 0 }));
          this._notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`online-orders-modal/notifications/${error.message}.Message`)
          );
        }
      });
  }
}
